import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentCodeParams
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class MultiRowFormula extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.multirowformula',
        group: 'Data Preparation',
        title: 'Multi-row formula',
        description: `
                        This component creates a new table containing a new column computed using a multi-row formula based on one or several input columns.

                        To refer to a value in the previous row, use {colname - 1} and to refer to a value in the next row, use {colname + 1}.
                        `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'colname',
          type: WorkflowComponentParamType.String,
          title: 'New column name',
          default: 'Newcol_multirow'
        },
        {
          name: 'coltype',
          type: WorkflowComponentParamType.Selection,
          title: 'New column type',
          options: ['Number', 'String'],
          default: 'Number'
        },
        {
          name: 'expression',
          type: WorkflowComponentParamType.StringSql,
          title: 'Expression',
          placeholder: '(col1 + {col1 - 1} + {col1 + 1}) / 3',
          allowExpressions: false
        },
        {
          name: 'nullvalues',
          type: WorkflowComponentParamType.Selection,
          title: 'Value for missing row values',
          options: ['Null', '0 or empty', 'First valid row in group'],
          default: 'Null'
        },
        {
          name: 'sortby',
          type: WorkflowComponentParamType.Column,
          title: 'Sort by',
          parent: 'source',
          dataType: [
            ColumnDataType.String,
            ColumnDataType.Number,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Boolean
          ]
        },
        {
          name: 'groupby',
          type: WorkflowComponentParamType.Column,
          title: 'Group by',
          parent: 'source',
          dataType: [
            ColumnDataType.String,
            ColumnDataType.Number,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Boolean
          ],
          mode: WorkflowComponentParameterMode.Multiple,
          optional: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const groupby = preparedParams.groupby as Array<string>
    const partition = groupby != undefined && groupby.length > 0 ? `PARTITION BY ${groupby.join(', ')}` : ''
    let expression = params.expression as string
    const re = /\{(.*?)\}/g
    const found = expression.match(re)
    const usedColumns: Array<string> = []
    const defaultValue =
      preparedParams.nullvalues === 'Null'
        ? 'NULL'
        : preparedParams.nullvalues === '0 or empty'
        ? preparedParams.coltype === 'Number'
          ? '0'
          : "''"
        : ''
    if (found) {
      for (let i = 0; i < found.length; i++) {
        const tokens = found[i].split(/[+-]/).map((x) => x.trim().replace('{', '').replace('}', ''))
        if (tokens.length == 2) {
          if (!usedColumns.includes(tokens[0])) {
            usedColumns.push(tokens[0])
          }
          expression = expression.replace(
            found[i],
            dedent`${found[i].includes('+') ? 'LEAD' : 'LAG'}(
                ${tokens[0]},
                ${tokens[1]}) OVER (${partition} ORDER BY ${params.sortby})`
          )
        }
      }
    }
    switch (connection.type) {
      case Provider.BigQuery: {
        let filled = ''
        if (preparedParams.nullvalues === 'First valid row in group') {
          filled = usedColumns
            .map(
              (x) => dedent`
                    COALESCE(
                        ${x},
                        FIRST_VALUE(${x} IGNORE NULLS) OVER (
                            ${partition}
                            ORDER BY ${params.sortby}
                            ROWS BETWEEN CURRENT ROW AND
                            UNBOUNDED FOLLOWING
                        )
                    ) AS ${x}`
            )
            .join(',\n ')
        } else if (preparedParams.nullvalues === '0 or empty') {
          filled = usedColumns.map((x) => `COALESCE(${x}, ${defaultValue}) AS ${x}`).join(',\n ')
        }

        let filledCTE = ''
        if (filled) {
          filledCTE = dedent`,
                        WITH __filled AS
                        (
                            SELECT * EXCEPT (${usedColumns.join(', ')}),
                                ${dedent(filled)}
                            FROM ${connection.quoteName(preparedParams.source as string)}
                        )`
        }
        const select = dedent`${filledCTE}
                                SELECT *,
                                    ${expression} AS ${preparedParams.colname}
                                FROM ${filledCTE ? '__filled' : connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        let filled = ''
        if (preparedParams.nullvalues === 'First valid row in group') {
          filled = usedColumns
            .map(
              (x) => dedent`
                    COALESCE(
                        ${x},
                        FIRST_VALUE(${x} IGNORE NULLS) OVER (
                            ${partition}
                            ORDER BY ${params.sortby}
                        )
                    ) AS ${x}`
            )
            .join(',\n ')
        } else if (preparedParams.nullvalues === '0 or empty') {
          filled = usedColumns.map((x) => `COALESCE(${x}, ${defaultValue}) AS ${x}`).join(',\n ')
        }

        let filledCTE = ''
        if (filled) {
          filledCTE = dedent`,
                        __filled AS
                        (
                            SELECT * EXCLUDE (${usedColumns.join(', ')}),
                                ${dedent(filled)}
                            FROM __numbered
                        )`
        }
        const select = dedent`${filledCTE}
                              SELECT *,
                                  ${expression} AS ${preparedParams.colname}
                              FROM ${filledCTE ? '__filled' : connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Redshift:
      case Provider.Postgres: {
        let filled = ''
        if (preparedParams.nullvalues === 'First valid row in group') {
          filled = usedColumns
            .map(
              (x) => dedent`
                    COALESCE(
                        ${x},
                        FIRST_VALUE(${x} IGNORE NULLS) OVER (
                            ${partition}
                            ORDER BY ${params.sortby}
                        )
                    ) AS ${x}__filled`
            )
            .join(',\n ')
        } else if (preparedParams.nullvalues === '0 or empty') {
          filled = usedColumns.map((x) => `COALESCE(${x}, ${defaultValue}) AS ${x}__filled`).join(',\n ')
        }

        let filledCTE = ''
        if (filled) {
          filledCTE = dedent`,
                        __filled AS
                        (
                            SELECT *,
                                ${dedent(filled)}
                            FROM __numbered
                        )`
        }
        const select = dedent`${filledCTE}
                              SELECT *,
                                  ${expression} AS ${preparedParams.colname}
                              FROM ${filledCTE ? '__filled' : connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        if (filled) {
          query +=
            '\n' +
            dedent`ALTER TABLE ${outputTablename}
            ${usedColumns.map((x) => `DROP COLUMN ${x}_filled`).join(',\n')};`
        }
        break
      }
      case Provider.DatabricksRest: {
        let filled = ''
        if (preparedParams.nullvalues === 'First valid row in group') {
          filled = usedColumns
            .map(
              (x) => dedent`
                    COALESCE(
                        ${x},
                        FIRST_VALUE(${x}, true) OVER (
                            ${partition}
                            ORDER BY ${params.sortby}
                            ROWS BETWEEN CURRENT ROW AND
                            UNBOUNDED FOLLOWING
                        )
                    ) AS ${x}`
            )
            .join(',\n ')
        } else if (preparedParams.nullvalues === '0 or empty') {
          filled = usedColumns.map((x) => `COALESCE(${x}, ${defaultValue}) AS ${x}`).join(',\n ')
        }

        let filledCTE = ''
        if (filled) {
          filledCTE = dedent`,
                        WITH __filled AS
                        (
                            SELECT /*+ REPARTITION(40) */
                              * EXCEPT (${usedColumns.join(', ')}),
                                ${dedent(filled)}
                            FROM ${connection.quoteName(preparedParams.source as string)}
                        )`
        }
        const select = dedent`${filledCTE}
                                SELECT /*+ REPARTITION(40) */
                                  *,
                                  ${expression} AS ${preparedParams.colname}
                                FROM ${filledCTE ? '__filled' : connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.Redshift, Provider.DatabricksRest]
  }
}

export { MultiRowFormula }
